// src/components/padrekko/qc/builder/useCaseBuilderActions.js
//
// Continue / Cancel for the case builder, shared by the banner and panel so the
// validation + navigation live in one place.
//
// The builder no longer creates the case. "Continue to case details" validates
// the draft pads, then deep-links to the PadRekko dashboard's NewCaseForm in
// from-selection mode (?newcase=selection) WITHOUT clearing the draft — the
// form reads the pads from useCaseBuilderStore, collects name + autodetect +
// participants, and POSTs /cases/from-selection. Cancel clears the draft and
// returns to the dashboard; an abandoned builder writes nothing.

import { useNavigate } from "@tanstack/react-router";
import { useCaseBuilderStore } from "../../../../stores/useCaseBuilderStore";

export function useCaseBuilderActions() {
  const navigate = useNavigate();

  const pads = useCaseBuilderStore((s) => s.pads);
  const returnTo = useCaseBuilderStore((s) => s.returnTo);
  const reset = useCaseBuilderStore((s) => s.reset);

  // ── derive continue-readiness + a single human reason for the first blocker ──
  // Pads only — name / participants / autodetect are collected in NewCaseForm.
  let blockReason = null;
  if (!pads.length) blockReason = "Select points to build pads";
  else {
    const names = new Set();
    for (const p of pads) {
      const n = (p.pad_name || "").trim();
      if (!n) { blockReason = "Every pad needs a name"; break; }
      if (names.has(n)) { blockReason = `Duplicate pad name "${n}"`; break; }
      names.add(n);
      if (p.region && p.region !== "ok" && p.region !== "pending") {
        blockReason = `Pad "${n}" is outside TX/NM`;
        break;
      }
      if (p.region === undefined || p.region === "pending") {
        blockReason = "Classifying pads…";
        break;
      }
    }
  }
  const canContinue = !blockReason;

  // Hand off to the case-details form. Keeps the draft in the store (persisted);
  // leaving the builder route flips it inactive via MapPage's enter/leave effect.
  function continueToDetails() {
    if (!canContinue) return;
    navigate({
      to: "/map/$tab",
      params: { tab: returnTo || "padrekko" },
      search: { newcase: "selection" },
    });
  }

  function cancel() {
    reset();
    navigate({
      to: "/map/$tab",
      params: { tab: returnTo || "padrekko" },
      search: {},
    });
  }

  return { continueToDetails, cancel, canContinue, blockReason };
}
